import React, { createContext, ReactNode, useCallback, useState } from 'react';
import { ReactKeycloakProvider } from '@react-keycloak/web';
import { AuthClientEvent } from '@react-keycloak/core';
import { KeycloakInitOptions } from 'keycloak-js';
import keycloak from './keycloak';
import { getAuthState, setAuthState, clearAuthState } from './state';

interface AuthProviderProps {
  children: ReactNode;
}

type TokensCallback = (tokens: {
  idToken?: string;
  refreshToken?: string;
  token?: string;
}) => void;

type AuthContextType = {
  initialized: boolean;
  authenticated: boolean;
};

export const AuthContext = createContext<AuthContextType>(
  {} as AuthContextType,
);

export function AuthProvider({ children }: AuthProviderProps) {
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const authState = getAuthState();

  const initOptions: KeycloakInitOptions = Object.assign(
    {
      checkLoginIframe: false,
      onLoad: undefined,
    },
    authState
      ? {
          token: authState.token,
          refreshToken: authState.refreshToken,
          timeSkew: authState.timeSkew,
        }
      : {},
  );

  const onTokens = useCallback<TokensCallback>((tokens) => {
    setInitialized(true);

    if (!tokens.token) {
      clearAuthState();
      setAuthenticated(false);

      return;
    }

    const impersonator = keycloak.tokenParsed?.impersonator as
      | { id: string }
      | undefined;

    setAuthState({
      refreshToken: `${tokens.refreshToken}`,
      token: tokens.token,
      timeSkew: keycloak.timeSkew as number,
      user: {
        userId: `${keycloak.tokenParsed?.sub}`,
        email: `${keycloak.tokenParsed?.email}`,
        // eslint-disable-next-line camelcase
        tenantId: `${keycloak.tokenParsed?.tenant_id}`,
        roles: keycloak.realmAccess?.roles,
        userType: keycloak.tokenParsed?.userType,
      },
      impersonator,
    });

    setAuthenticated(true);
  }, []);

  const onEvent = useCallback((event: AuthClientEvent) => {
    if (event === 'onAuthLogout') {
      setAuthenticated(false);
    }
  }, []);

  const context: AuthContextType = {
    initialized,
    authenticated,
  };

  return (
    <AuthContext.Provider value={context}>
      <ReactKeycloakProvider
        authClient={keycloak}
        initOptions={initOptions}
        onTokens={onTokens}
        onEvent={onEvent}
      >
        {children}
      </ReactKeycloakProvider>
    </AuthContext.Provider>
  );
}
